/**
 * Value Collection Store (Brief E)
 *
 * Manages named, versioned, owner-attributed sets of value judgments.
 * Supports:
 *  - selectValueSource: assign a published collection to a track
 *  - lockTrack: freeze values (manual or automatic); snapshots at lock time
 *  - lockCycle: lock all tracks in a cycle simultaneously
 *  - fetchValueCollections: load published collections for the workspace
 */

import { create } from 'zustand';
import { getSafeSupabaseClient, getCurrentWorkspaceId } from '../../lib/supabaseSync';

// ============================================================================
// Types
// ============================================================================

export interface ValueCollection {
  id: string;
  workspaceId: string;
  ownerId: string;
  ownerName?: string;
  name: string;
  description: string | null;
  version: number;
  status: 'draft' | 'published' | 'locked';
  lockedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface ValueCollectionSnapshot {
  id: string;
  valueCollectionId: string | null;
  trackId: string;
  cycleId: string | null;
  workspaceId: string;
  snapshotData: unknown[];
  selectedBy: string;
  selectedAt: Date;
  lockedAt: Date;
  isWorkspaceFallback: boolean;
  createdAt: Date;
}

interface SelectValueSourceParams {
  trackId: string;
  /**
   * The published collection to use, or null to clear the choice — the Track
   * then uses the workspace's official values at lock time (roadmap 1C, V-9).
   */
  valueCollectionId: string | null;
  actorId: string;
  workspaceId: string;
}

interface LockTrackParams {
  trackId: string;
  actorId: string;
  workspaceId: string;
  /** When true, skips S1-S4 prerequisite check and writes track_locked_automatic event */
  isAutomatic?: boolean;
}

interface LockCycleParams {
  cycleId: string;
  actorId: string;
  workspaceId: string;
}

interface ValueCollectionStore {
  collections: ValueCollection[];
  isLoading: boolean;
  error: string | null;

  /** Fetch all published (and draft/locked) collections visible to the workspace. */
  fetchValueCollections: (workspaceId: string) => Promise<void>;

  /** Assign a published collection as the value source for a track (before lock). */
  selectValueSource: (params: SelectValueSourceParams) => Promise<boolean>;

  /** Manually lock a track's values (snapshot + governance event). */
  lockTrack: (params: LockTrackParams) => Promise<boolean>;

  /** Lock all unlocked tracks in a cycle simultaneously. */
  lockCycle: (params: LockCycleParams) => Promise<boolean>;

  clearError: () => void;
}

// ============================================================================
// RPC caller for lock_track_values (migration 318)
// ============================================================================

interface LockTrackRpcResult {
  success: boolean;
  error?: string;
  already_locked?: boolean;
  values_converged_at?: string;
  snapshot_id?: string;
  is_workspace_fallback?: boolean;
  event_type?: string;
  readiness?: unknown;
}

async function callLockTrackValuesRpc(params: {
  trackId: string;
  actorId: string;
  workspaceId: string;
  isAutomatic: boolean;
}): Promise<LockTrackRpcResult> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return { success: false, error: 'Supabase not available' };

  // Convert 'system' pseudo-actor to null (RPC handles null actor for automatic locks)
  const actorUuid = params.actorId === 'system' ? null : params.actorId;

  const { data, error } = await supabase.rpc('lock_track_values', {
    p_track_id: params.trackId,
    p_actor_id: actorUuid,
    p_workspace_id: params.workspaceId,
    p_is_automatic: params.isAutomatic,
  });

  if (error) {
    console.error('[ValueCollection] lock_track_values RPC error:', error);
    return { success: false, error: error.message };
  }

  return (data as LockTrackRpcResult) ?? { success: false, error: 'No response from RPC' };
}

// ============================================================================
// Store
// ============================================================================

export const useValueCollectionStore = create<ValueCollectionStore>()((set, get) => ({
  collections: [],
  isLoading: false,
  error: null,

  // --------------------------------------------------------------------------
  fetchValueCollections: async (workspaceId: string): Promise<void> => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('value_collections')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[ValueCollectionStore] fetch error:', error);
        set({ error: error.message, isLoading: false });
        return;
      }

      const collections: ValueCollection[] = (data ?? []).map(r => ({
        id: r.id,
        workspaceId: r.workspace_id,
        ownerId: r.owner_id,
        name: r.name,
        description: r.description,
        version: r.version,
        status: r.status,
        lockedAt: r.locked_at ? new Date(r.locked_at) : null,
        createdAt: new Date(r.created_at),
        updatedAt: new Date(r.updated_at),
      }));

      set({ collections, isLoading: false });
    } catch (err) {
      console.error('[ValueCollectionStore] fetchValueCollections exception:', err);
      set({ error: String(err), isLoading: false });
    }
  },

  // --------------------------------------------------------------------------
  selectValueSource: async (params: SelectValueSourceParams): Promise<boolean> => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;

    // Guard: track must not already be locked (Phase C-1: the lock stamp
    // lives on session_baselines, the sole home of Baseline content)
    const { data: track } = await supabase
      .from('session_baselines')
      .select('values_converged_at')
      .eq('track_id', params.trackId)
      .maybeSingle();

    if (track?.values_converged_at) {
      console.warn('[ValueCollection] Cannot select value source: track is already locked');
      return false;
    }

    const clearing = params.valueCollectionId === null;

    // Guard: collection must be published (not draft). Clearing has no collection.
    if (!clearing) {
      const { data: collection } = await supabase
        .from('value_collections')
        .select('status')
        .eq('id', params.valueCollectionId)
        .single();

      if (!collection || collection.status === 'draft') {
        console.warn('[ValueCollection] Cannot select: collection is not published');
        return false;
      }
    }

    const now = new Date().toISOString();

    // Phase C-1 (canon 28 §6, migration 596): the Session Baseline is the
    // sole home of the value-source binding; the mirrored tracks columns
    // were dropped. Clearing is the same write with no collection: the
    // selection window is still open (checked above), and the canon fallback
    // — the workspace's official values at lock time — applies again. Who
    // cleared it, and when, stays on the row and in the event.
    const { error } = await supabase
      .from('session_baselines')
      .update({
        value_collection_id: params.valueCollectionId,
        value_source_selected_by: params.actorId,
        value_source_selected_at: now,
      })
      .eq('track_id', params.trackId);

    if (error) {
      console.error('[ValueCollection] Failed to select value source:', error);
      return false;
    }

    void supabase.from('governance_events').insert({
      event_type: clearing ? 'value_source_cleared' : 'value_source_selected',
      actor_id: params.actorId,
      occurred_at: now,
      note: clearing
        ? `Value collection cleared for track ${params.trackId}; the workspace's official values apply at lock time`
        : `Value collection ${params.valueCollectionId} selected for track ${params.trackId}`,
      workspace_id: params.workspaceId,
    });

    // Refresh local collections list so UI reflects any status change
    void get().fetchValueCollections(params.workspaceId);

    return true;
  },

  // --------------------------------------------------------------------------
  lockTrack: async (params: LockTrackParams): Promise<boolean> => {
    const result = await callLockTrackValuesRpc({
      trackId: params.trackId,
      actorId: params.actorId,
      workspaceId: params.workspaceId,
      isAutomatic: params.isAutomatic ?? false,
    });

    if (!result.success) {
      console.error('[ValueCollection] lockTrack failed:', result.error, result.readiness ?? '');
      return false;
    }

    // Refresh local collections so locked status propagates to UI
    void get().fetchValueCollections(params.workspaceId);

    return true;
  },

  // --------------------------------------------------------------------------
  lockCycle: async (params: LockCycleParams): Promise<boolean> => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;

    // Phase C-1: values_converged_at lives on session_baselines; embed it.
    const { data: tracks } = await supabase
      .from('tracks')
      .select('id, session_baselines(values_converged_at)')
      .eq('cycle_id', params.cycleId);

    if (!tracks?.length) return false;

    const baselineOf = (t: { session_baselines?: { values_converged_at: string | null } | { values_converged_at: string | null }[] | null }) => {
      const sb = t.session_baselines;
      return Array.isArray(sb) ? sb[0] : sb;
    };
    const unlocked = tracks.filter(t => !baselineOf(t)?.values_converged_at);
    if (!unlocked.length) return true; // all already locked — idempotent

    const results = await Promise.all(
      unlocked.map(t =>
        get().lockTrack({
          trackId: t.id,
          actorId: params.actorId,
          workspaceId: params.workspaceId,
          isAutomatic: false,
        })
      )
    );

    return results.every(Boolean);
  },

  // --------------------------------------------------------------------------
  clearError: () => set({ error: null }),
}));

// ============================================================================
// Standalone lockTrack (for use from sessionStore without circular imports)
// ============================================================================

/**
 * Standalone lockTrack function usable outside the Zustand store context,
 * e.g. from sessionStore's RUN_COMPLETE trigger.
 */
export async function lockTrackStandalone(params: LockTrackParams): Promise<boolean> {
  const workspaceId = params.workspaceId || getCurrentWorkspaceId() || '';
  return useValueCollectionStore.getState().lockTrack({ ...params, workspaceId });
}
